import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ClientLogo from "./ClientLogo";

function StatusChip({ status }) {
    if (status === "active") {
        return (
            <span className="lt-chip lt-chip--active" data-testid="status-chip-active">
                <span className="lt-chip-dot" />Attiva
            </span>
        );
    }
    return <span className="lt-chip lt-chip--inactive" data-testid="status-chip-inactive">Inattiva</span>;
}

function useDebounce(v, d = 300) {
    const [x, setX] = useState(v);
    useEffect(() => { const t = setTimeout(() => setX(v), d); return () => clearTimeout(t); }, [v, d]);
    return x;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
}

export default function ClientList() {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const debouncedQ = useDebounce(q, 300);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status, page: 1, page_size: 50 };
            if (debouncedQ) params.q = debouncedQ;
            const { data } = await api.get("/internal/clients", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error("Errore nel caricamento delle società clienti");
        } finally {
            setLoading(false);
        }
    }, [debouncedQ, status]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const empty = !loading && items.length === 0 && !debouncedQ && status === "all";

    return (
        <div style={{ padding: "32px 40px" }} data-testid="clients-list-page">
            <div className="flex items-start justify-between" style={{ marginBottom: "28px", flexWrap: "wrap", gap: "16px" }}>
                <div>
                    <h1 className="lt-h1" data-testid="clients-heading">Società clienti</h1>
                    <p className="lt-sub" style={{ marginTop: "6px" }}>{total} {total === 1 ? "società" : "società"}</p>
                </div>
                <button type="button" className="lt-btn-primary" onClick={() => navigate("/app/clienti/nuova")} data-testid="clients-new-btn">
                    <Plus size={16} strokeWidth={2.25} /> Nuova società
                </button>
            </div>

            <div className="flex items-center" style={{ gap: "20px", marginBottom: "20px", flexWrap: "wrap" }}>
                <div style={{ position: "relative", width: "360px", maxWidth: "100%" }}>
                    <Search size={16} strokeWidth={1.75}
                        style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#8A8F88", pointerEvents: "none" }} />
                    <input
                        type="text"
                        className="lt-field"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca per ragione sociale o P.IVA / CF…"
                        data-testid="clients-search-input"
                        style={{ paddingLeft: "40px", marginBottom: 0 }}
                    />
                </div>
                <div className="lt-seg" data-testid="clients-status-filter">
                    {[
                        { k: "all", l: "Tutte" },
                        { k: "active", l: "Attive" },
                        { k: "inactive", l: "Inattive" },
                    ].map((it) => (
                        <button key={it.k} type="button"
                            className={status === it.k ? "is-active" : ""}
                            onClick={() => setStatus(it.k)}
                            data-testid={`clients-filter-${it.k}`}>
                            {it.l}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div style={{ padding: "48px", textAlign: "center", color: "#8A8F88", fontSize: "14px" }} data-testid="clients-loading">
                    Caricamento…
                </div>
            ) : empty ? (
                <div className="lt-card" style={{ padding: "48px", textAlign: "center" }} data-testid="clients-empty">
                    <p className="lt-sub" style={{ marginBottom: "16px" }}>Nessuna società cliente. Inizia creandone una.</p>
                    <button type="button" className="lt-btn-primary" onClick={() => navigate("/app/clienti/nuova")} data-testid="clients-empty-new-btn">
                        <Plus size={16} strokeWidth={2.25} /> Nuova società
                    </button>
                </div>
            ) : items.length === 0 ? (
                <div className="lt-card" style={{ padding: "32px", textAlign: "center" }}>
                    <p className="lt-sub">Nessun risultato per la ricerca.</p>
                </div>
            ) : (
                <div className="lt-table-wrap">
                    <table className="lt-table" data-testid="clients-table">
                        <thead>
                            <tr>
                                <th style={{ width: "56px" }}>Logo</th>
                                <th>Ragione sociale</th>
                                <th>P.IVA o CF</th>
                                <th>PEC</th>
                                <th>Referente interno</th>
                                <th style={{ textAlign: "right" }}>Utenti</th>
                                <th>Stato</th>
                                <th>Creato il</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} onClick={() => navigate(`/app/clienti/${c.id}`)} data-testid={`client-row-${c.id}`}>
                                    <td>
                                        <ClientLogo client={c} size={32} />
                                    </td>
                                    <td style={{ fontWeight: 500 }}>{c.company_name}</td>
                                    <td style={{ color: "#5F6560", fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px" }}>{c.vat_or_taxcode}</td>
                                    <td style={{ color: "#5F6560" }}>{c.pec_email}</td>
                                    <td style={{ color: "#5F6560" }}>{c.assigned_internal_user?.name || "—"}</td>
                                    <td style={{ textAlign: "right", color: "#5F6560" }}>{c.client_users_count}</td>
                                    <td><StatusChip status={c.status} /></td>
                                    <td style={{ color: "#5F6560" }}>{fmtDate(c.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
